import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCustomFieldDefinitions } from "@/hooks/useCustomFieldDefinitions";
import { useAuth } from "@/state/AuthContext";
import {
  ENTITY_TYPE_LABELS,
  FIELD_TYPE_LABELS,
  type CustomFieldDefinition,
  type EntityType,
  type FieldType,
} from "@/types/customFields";
import { hasPermission, PERMISSIONS } from "@/utils/permissions";
import { apiFetch } from "@/utils/api";

const ENTITY_TYPES: EntityType[] = ["part", "system", "location"];
const FIELD_TYPES: FieldType[] = ["text", "number", "date", "boolean", "select"];

function entityTypesForUser(user: ReturnType<typeof useAuth>["user"]): EntityType[] {
  const types: EntityType[] = [];
  if (hasPermission(user, PERMISSIONS.adminParts)) {
    types.push("part", "location");
  }
  if (hasPermission(user, PERMISSIONS.adminSystems)) {
    types.push("system");
  }
  return types.length ? types : ENTITY_TYPES;
}

const emptyForm = {
  fieldLabel: "",
  fieldType: "text" as FieldType,
  required: false,
  optionsText: "",
  sortOrder: 0,
};

function slugifyFieldKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
}

interface FieldFormProps {
  form: typeof emptyForm;
  onFormChange: (next: typeof emptyForm) => void;
  formError: string;
  saving: boolean;
  onSubmit: (e: FormEvent) => void;
  submitLabel: string;
  onCancel: () => void;
}

function FieldForm({
  form,
  onFormChange,
  formError,
  saving,
  onSubmit,
  submitLabel,
  onCancel,
}: FieldFormProps) {
  return (
    <form className="bg-white rounded-lg shadow p-4 grid gap-3 sm:grid-cols-2 max-w-2xl" onSubmit={onSubmit}>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="field-label">Field label *</Label>
        <Input
          id="field-label"
          value={form.fieldLabel}
          onChange={(e) => onFormChange({ ...form, fieldLabel: e.target.value })}
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="field-type">Type</Label>
        <Select
          id="field-type"
          value={form.fieldType}
          onChange={(e) => onFormChange({ ...form, fieldType: e.target.value as FieldType })}
        >
          {FIELD_TYPES.map((type) => (
            <option key={type} value={type}>
              {FIELD_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="field-sort">Sort order</Label>
        <Input
          id="field-sort"
          type="number"
          value={form.sortOrder}
          onChange={(e) => onFormChange({ ...form, sortOrder: Number(e.target.value) || 0 })}
        />
      </div>
      {form.fieldType === "select" && (
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="field-options">Dropdown options (one per line) *</Label>
          <textarea
            id="field-options"
            className="flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.optionsText}
            onChange={(e) => onFormChange({ ...form, optionsText: e.target.value })}
            required
          />
        </div>
      )}
      <div className="flex items-center justify-between sm:col-span-2">
        <Label htmlFor="field-required">Required when saving records</Label>
        <Switch
          id="field-required"
          checked={form.required}
          onCheckedChange={(checked) => onFormChange({ ...form, required: checked })}
        />
      </div>
      {formError && <p className="text-sm text-destructive sm:col-span-2">{formError}</p>}
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" disabled={saving} className="bg-[#1e3a4f] hover:bg-[#163041]">
          {saving ? "Saving…" : submitLabel}
        </Button>
        <Button type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function AdminCustomFieldsPage() {
  const { user } = useAuth();
  const allowedTypes = entityTypesForUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedType = searchParams.get("type") as EntityType;
  const initialType = allowedTypes.includes(requestedType) ? requestedType : allowedTypes[0];
  const [entityType, setEntityType] = useState<EntityType>(initialType);
  const { definitions, loading, error, reload } = useCustomFieldDefinitions(entityType);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSearchParams({ type: entityType }, { replace: true });
    setShowNew(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
  }, [entityType, setSearchParams]);

  const startEdit = (field: CustomFieldDefinition) => {
    setShowNew(false);
    setEditingId(field.id);
    setForm({
      fieldLabel: field.fieldLabel,
      fieldType: field.fieldType,
      required: field.required,
      optionsText: field.options.join("\n"),
      sortOrder: field.sortOrder,
    });
    setFormError("");
  };

  const cancelForm = () => {
    setShowNew(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
  };

  const payloadFromForm = () => ({
    entityType,
    fieldLabel: form.fieldLabel,
    fieldKey: slugifyFieldKey(form.fieldLabel),
    fieldType: form.fieldType,
    required: form.required,
    options:
      form.fieldType === "select"
        ? form.optionsText
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
        : [],
    sortOrder: form.sortOrder,
  });

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      await apiFetch("/admin/custom-fields", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadFromForm()),
      });
      cancelForm();
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create field");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setFormError("");
    setSaving(true);
    try {
      const { fieldLabel, fieldType, required, options, sortOrder } = payloadFromForm();
      await apiFetch(`/admin/custom-fields/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fieldLabel, fieldType, required, options, sortOrder }),
      });
      cancelForm();
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update field");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this custom field? Stored values will also be removed.")) return;
    setFormError("");
    try {
      await apiFetch(`/admin/custom-fields/${id}`, { method: "DELETE" });
      if (editingId === id) cancelForm();
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to delete field");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const hasSelectFields = definitions.some((f) => f.fieldType === "select");

  const tableColumns = [
    { key: "label", header: "Label", render: (f: CustomFieldDefinition) => f.fieldLabel },
    { key: "type", header: "Type", render: (f: CustomFieldDefinition) => FIELD_TYPE_LABELS[f.fieldType] },
    { key: "req", header: "Required", render: (f: CustomFieldDefinition) => (f.required ? "Yes" : "No") },
    ...(hasSelectFields
      ? [
          {
            key: "opts",
            header: "Options",
            render: (f: CustomFieldDefinition) =>
              f.fieldType === "select" && f.options.length ? f.options.join(", ") : "—",
          },
        ]
      : []),
    {
      key: "actions",
      header: "",
      render: (f: CustomFieldDefinition) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" disabled={editingId === f.id} onClick={() => startEdit(f)}>
            Edit
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void remove(f.id)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Custom Fields"
        subtitle="Define extra fields for parts, systems, and locations. Values are entered when creating or editing each record."
        actions={
          !showNew && !editingId ? (
            <Button
              type="button"
              onClick={() => {
                setShowNew(true);
                setForm(emptyForm);
                setFormError("");
              }}
              className="bg-[#1e3a4f] hover:bg-[#163041]"
            >
              Add Field
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap gap-2">
        {allowedTypes.map((type) => (
          <Button
            key={type}
            type="button"
            size="sm"
            variant={entityType === type ? "default" : "secondary"}
            className={entityType === type ? "bg-[#1e3a4f] hover:bg-[#163041]" : undefined}
            onClick={() => setEntityType(type)}
          >
            {ENTITY_TYPE_LABELS[type]}
          </Button>
        ))}
      </div>

      <p className="text-sm text-muted-foreground">
        Manage values on the{" "}
        <Link to={`/admin/${entityType === "part" ? "parts" : entityType === "system" ? "systems" : "locations"}`} className="underline text-[#1e3a4f]">
          {ENTITY_TYPE_LABELS[entityType]}
        </Link>{" "}
        admin page when creating or editing records.
      </p>

      {showNew && (
        <FieldForm
          form={form}
          onFormChange={setForm}
          formError={formError}
          saving={saving}
          onSubmit={(e) => void create(e)}
          submitLabel="Create field"
          onCancel={cancelForm}
        />
      )}
      {editingId && (
        <FieldForm
          form={form}
          onFormChange={setForm}
          formError={formError}
          saving={saving}
          onSubmit={(e) => void saveEdit(e)}
          submitLabel="Save changes"
          onCancel={cancelForm}
        />
      )}

      {formError && !showNew && !editingId && <p className="text-sm text-destructive">{formError}</p>}

      <DataTable
        columns={tableColumns}
        rows={definitions}
        rowKey={(f) => f.id}
        emptyMessage={`No custom fields for ${ENTITY_TYPE_LABELS[entityType].toLowerCase()} yet.`}
      />
    </div>
  );
}
